/**
 * S-3057E Fixer: V2 overlay that uses V1's exact getFrameAtTime logic
 * S-3058A: Visual parity with V1 using shared theme
 * S-3058 FIXER: Synchronous filtering to reduce jitter/accordion/discontinuities
 * 
 * The key insight: V1 already has getFrameAtTime() that does:
 * - Frame lookup by timestamp
 * - Linear interpolation between frames
 * - Returns wrapped format: { pose: { landmarks: [[x,y,z,vis]] } }
 * 
 * V2 adds synchronous filtering (NO async, NO worker):
 * - EMA smoothing (reduces jitter)
 * - Outlier rejection (prevents jumps)
 * - Limb length stabilization (reduces accordion)
 * 
 * All filters run synchronously (<1ms) in draw() callback.
 */

console.log('[V2Simple] 🎯 MODULE LOADED - Using V1 getFrameAtTime + sync filters');

import React, { useRef, useEffect, useCallback } from 'react';
import { getContentRect } from '../swing-lab/lib/video/getContentRect';
import { OVERLAY_THEME_V1 } from '../telemetry/theme/overlayThemeV1';
import { SyncPoseFilter, DEFAULT_SYNC_FILTER_CONFIG } from './syncFilters';

// S-3058A: Use V1 theme for body part connections
// Grouped by body part to match V1's drawing order and colors
const TORSO_CONNECTIONS: [number, number][] = [
  [11, 12], // Shoulders
  [11, 23], // Left shoulder to left hip
  [12, 24], // Right shoulder to right hip
  [23, 24], // Hips
];

const LEFT_ARM_CONNECTIONS: [number, number][] = [
  [11, 13], // Left shoulder to left elbow
  [13, 15], // Left elbow to left wrist
];

const LEFT_LEG_CONNECTIONS: [number, number][] = [
  [23, 25], // Left hip to left knee
  [25, 27], // Left knee to left ankle
  [27, 29], // Left ankle to left heel
  [27, 31], // Left ankle to left foot index
  [29, 31], // Left heel to left foot index
];

const RIGHT_ARM_CONNECTIONS: [number, number][] = [
  [12, 14], // Right shoulder to right elbow
  [14, 16], // Right elbow to right wrist
];

const RIGHT_LEG_CONNECTIONS: [number, number][] = [
  [24, 26], // Right hip to right knee
  [26, 28], // Right knee to right ankle
  [28, 30], // Right ankle to right heel
  [28, 32], // Right ankle to right foot index
  [30, 32], // Right heel to right foot index
];

interface OverlayCanvasV2SimpleProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  getFrameAtTime: (time: number) => any; // Use V1's function
  visible: boolean;
  opacity: number;
}

export function OverlayCanvasV2Simple({
  videoRef,
  getFrameAtTime,
  visible,
  opacity,
}: OverlayCanvasV2SimpleProps) {
  console.log('[V2Simple] 🚀 COMPONENT RENDERING', { visible, opacity });
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameCountRef = useRef(0);
  
  // S-3058 FIXER: Synchronous filter (NO async, NO worker)
  const filterRef = useRef<SyncPoseFilter>(
    new SyncPoseFilter(DEFAULT_SYNC_FILTER_CONFIG)
  );

  // Reset filter on video change
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleSeeked = () => {
        console.log('[V2Simple] Video seeked, resetting filters');
        filterRef.current.reset();
      };
      video.addEventListener('seeked', handleSeeked);
      return () => video.removeEventListener('seeked', handleSeeked);
    }
  }, [videoRef]);

  // Draw skeleton (V1 getFrameAtTime + sync filtering + V1 theme)
  const draw = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas || !visible) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get pose at current video time (using V1's getFrameAtTime)
    const currentTime = video.currentTime;
    const frameData = getFrameAtTime(currentTime);
    
    if (!frameData?.pose?.landmarks) {
      return;
    }

    // V1 returns landmarks as [[x, y, z, vis], ...]
    const rawLandmarks = frameData.pose.landmarks[0]; // First person
    
    if (!rawLandmarks || rawLandmarks.length < 33) {
      return;
    }

    // S-3058 FIXER: Apply synchronous filtering (reduces jitter/accordion/discontinuities)
    const landmarks = filterRef.current.filter(rawLandmarks);

    // CRITICAL: Calculate content rectangle (actual video content, excluding letterbox)
    const contentRect = getContentRect(video);

    // Clear canvas (in CSS pixel space after DPR transform)
    ctx.clearRect(0, 0, video.clientWidth, video.clientHeight);
    
    // S-3058A: Apply V1 theme alpha and context defaults
    const normalizedOpacity = Math.max(0, Math.min(1, opacity / 100));
    ctx.globalAlpha = normalizedOpacity * OVERLAY_THEME_V1.alpha.stroke;
    ctx.lineCap = OVERLAY_THEME_V1.ctx.lineCap;
    ctx.lineJoin = OVERLAY_THEME_V1.ctx.lineJoin;
    ctx.miterLimit = OVERLAY_THEME_V1.ctx.miterLimit;

    // Helper to draw connections for a body part
    const drawConnections = (
      connections: [number, number][],
      color: string,
      lineWidth: number
    ) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      
      for (const [a, b] of connections) {
        const A = landmarks[a];
        const B = landmarks[b];
        if (A && B && A[0] !== undefined && B[0] !== undefined) {
          // Map normalized (0-1) to content rect pixels (same as V1's normToPixels formula)
          const ax = contentRect.x + A[0] * contentRect.w;
          const ay = contentRect.y + A[1] * contentRect.h;
          const bx = contentRect.x + B[0] * contentRect.w;
          const by = contentRect.y + B[1] * contentRect.h;
          
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
        }
      }
      ctx.stroke();
    };

    // S-3058A: Draw skeleton using V1 theme colors but V2 line width (3px)
    // Drawing order matters for visual parity!
    // S-3058A-FIXER: User requested thicker lines (3px) from previous V2 instead of V1's thinner lines
    const lineWidth = 3; // Previous V2 line width
    
    // 1. Torso (base layer)
    drawConnections(
      TORSO_CONNECTIONS,
      OVERLAY_THEME_V1.colors.torso,
      lineWidth
    );

    // 2. Left arm
    drawConnections(
      LEFT_ARM_CONNECTIONS,
      OVERLAY_THEME_V1.colors.leftArm,
      lineWidth
    );

    // 3. Left leg
    drawConnections(
      LEFT_LEG_CONNECTIONS,
      OVERLAY_THEME_V1.colors.leftLeg,
      lineWidth
    );

    // 4. Right arm
    drawConnections(
      RIGHT_ARM_CONNECTIONS,
      OVERLAY_THEME_V1.colors.rightArm,
      lineWidth
    );

    // 5. Right leg
    drawConnections(
      RIGHT_LEG_CONNECTIONS,
      OVERLAY_THEME_V1.colors.rightLeg,
      lineWidth
    );

    // 6. Joints on top (S-3058A: Use V1 theme for joint rendering)
    ctx.globalAlpha = normalizedOpacity * OVERLAY_THEME_V1.alpha.joint;
    ctx.fillStyle = OVERLAY_THEME_V1.colors.joint;
    
    for (let i = 0; i < landmarks.length; i++) {
      const landmark = landmarks[i];
      if (landmark && landmark[0] !== undefined) {
        const x = contentRect.x + landmark[0] * contentRect.w;
        const y = contentRect.y + landmark[1] * contentRect.h;
        
        ctx.beginPath();
        ctx.arc(x, y, OVERLAY_THEME_V1.joint.radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    }

    // Log every 60 frames
    frameCountRef.current++;
    if (frameCountRef.current % 60 === 0) {
      console.log('[V2Simple] Rendered', { 
        frames: frameCountRef.current, 
        time: currentTime.toFixed(3),
        landmarksCount: landmarks.length,
        contentRect
      });
    }
  }, [videoRef, getFrameAtTime, visible, opacity]);

  // Recursive draw loop using rVFC (EXACTLY like V1)
  const scheduleNext = useCallback(() => {
    const video = videoRef.current;
    
    if (video && visible && !video.paused && !video.ended) {
      video.requestVideoFrameCallback(() => {
        draw();
        scheduleNext();
      });
    }
  }, [visible, draw]);

  // Start/stop drawing on play/pause (EXACTLY like V1)
  useEffect(() => {
    console.log('[V2Simple] Mount effect', {
      hasVideo: !!videoRef.current,
      hasCanvas: !!canvasRef.current,
      visible,
      videoPaused: videoRef.current?.paused,
      videoEnded: videoRef.current?.ended,
      videoReadyState: videoRef.current?.readyState,
      videoCurrentTime: videoRef.current?.currentTime
    });
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas || !visible) {
      console.log('[V2Simple] Mount skipped - missing resources');
      return;
    }

    // Size canvas to match video (with DPR, exactly like V1)
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const canvasW = Math.round(video.clientWidth * dpr);
      const canvasH = Math.round(video.clientHeight * dpr);
      
      if (canvas.width !== canvasW || canvas.height !== canvasH) {
        canvas.width = canvasW;
        canvas.height = canvasH;
        canvas.style.width = `${video.clientWidth}px`;
        canvas.style.height = `${video.clientHeight}px`;
      }
      
      // Apply DPR transform to ctx (CRITICAL - V1 does this!)
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    };

    const handlePlay = () => {
      console.log('[V2Simple] Video playing event fired, starting draw loop');
      scheduleNext();
    };
    
    const handlePlaying = () => {
      console.log('[V2Simple] Video "playing" event fired, starting draw loop');
      scheduleNext();
    };

    resizeCanvas();
    video.addEventListener('loadedmetadata', resizeCanvas);
    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlaying);

    // CRITICAL: Start immediately if already playing
    if (!video.paused && !video.ended) {
      console.log('[V2Simple] Video ALREADY playing on mount, starting loop immediately', {
        paused: video.paused,
        ended: video.ended,
        currentTime: video.currentTime
      });
      scheduleNext();
    } else {
      console.log('[V2Simple] Video NOT playing on mount, waiting for play event', {
        paused: video.paused,
        ended: video.ended
      });
    }

    return () => {
      console.log('[V2Simple] Unmounting');
      video.removeEventListener('loadedmetadata', resizeCanvas);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [videoRef, visible, scheduleNext]);

  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      data-testid="overlay-v2"
      data-style="v1-parity"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
      aria-label="Golf swing pose overlay (v2) - frame-accurate rendering with V1 theme"
      role="img"
      tabIndex={-1}
    />
  );
}
